/**
 * Cut a printed sheet of stamps into single stamps with transparent
 * backgrounds, in the browser (admin "Stamps" page).
 *
 * Tuned on the original sheets — about 1024px, a beige ground, white perforated
 * stamps in a grid, soft drop shadows — and kept to plain pixel arithmetic so
 * it runs on any canvas `ImageData` (and in tests without one):
 *
 *  1. The ground is the median colour of the top and bottom 8 rows.
 *  2. A pixel is "ink" when it differs from the ground by more than 30 (sum of
 *     |ΔR|+|ΔG|+|ΔB|). Grid rows / columns are runs where the mean ink along
 *     the other axis is above 0.15 and that are at least 60px long.
 *  3. Each cell is cropped with 10px of padding, then masked: for thresholds
 *     12, 8, 5, 3, the stamp's paper is everything lighter than the ground by
 *     that much; the paper is dilated 3px (closing the perforations), the
 *     ground is flood-filled in from the crop's edge through anything that is
 *     not paper, and what the fill cannot reach is the stamp. That is eroded
 *     3px back and feathered with a 0.8px blur. The first threshold whose stamp
 *     covers more than 70% of the crop wins — the shadow is ground-coloured
 *     enough to be filled away, and the perforated edge survives. If none
 *     does (the ground is lighter than the paper, e.g. cream stamps on white),
 *     the same runs again with "paper" meaning any colour that differs from
 *     the ground by 36, 27, 20 or 14.
 *  4. Only the largest connected piece is kept (a stray speck beside a stamp
 *     would widen the crop), and the result is cropped to its alpha's box.
 */

export interface Pixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export interface CutStamp {
  /** RGBA, alpha applied — ready for `new ImageData(…)`. */
  pixels: Pixels;
  /** Where it came from on the sheet (before trimming to the alpha box). */
  row: number;
  col: number;
  /** No threshold reached the 70% cover: the cut is a best effort — worth a look. */
  rough: boolean;
}

const EDGE_ROWS = 8;
const INK_DIFF = 30;
const BAND_MEAN = 0.15;
const BAND_MIN = 60;
const PAD = 10;
const THRESHOLDS = [12, 8, 5, 3];
/**
 * Second pass, for a sheet whose ground is lighter than the stamps' paper
 * (cream stamps on a near-white table): paper is anything whose colour differs
 * from the ground by this much (|ΔR|+|ΔG|+|ΔB|). Strictest first, so a faint
 * shadow stays ground where it can.
 */
const COLOUR_THRESHOLDS = [36, 27, 20, 14];
const GROW = 3;
const COVER = 0.7;
const FEATHER = 0.8;

function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] ?? 0;
}

/** The ground colour: median R, G, B over the top and bottom edge rows. */
export function groundColour(img: Pixels): [number, number, number] {
  const r: number[] = [];
  const g: number[] = [];
  const b: number[] = [];
  const rows = Math.min(EDGE_ROWS, Math.floor(img.height / 2));
  const take = (y: number) => {
    for (let x = 0; x < img.width; x++) {
      const i = (y * img.width + x) * 4;
      r.push(img.data[i]!);
      g.push(img.data[i + 1]!);
      b.push(img.data[i + 2]!);
    }
  };
  for (let y = 0; y < rows; y++) take(y);
  for (let y = img.height - rows; y < img.height; y++) take(y);
  return [median(r), median(g), median(b)];
}

/** Runs of indexes where `mean[i] > BAND_MEAN`, at least BAND_MIN long. */
export function bands(mean: Float32Array, min = BAND_MIN): [number, number][] {
  const out: [number, number][] = [];
  let start = -1;
  for (let i = 0; i <= mean.length; i++) {
    const on = i < mean.length && mean[i]! > BAND_MEAN;
    if (on && start < 0) start = i;
    if (!on && start >= 0) {
      if (i - start >= min) out.push([start, i]);
      start = -1;
    }
  }
  return out;
}

/** The grid: row and column bands of ink against the ground. */
export function findGrid(img: Pixels, bg: [number, number, number]): { rows: [number, number][]; cols: [number, number][] } {
  const { width: w, height: h, data } = img;
  const rowMean = new Float32Array(h);
  const colMean = new Float32Array(w);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const d = Math.abs(data[i]! - bg[0]) + Math.abs(data[i + 1]! - bg[1]) + Math.abs(data[i + 2]! - bg[2]);
      if (d > INK_DIFF) {
        rowMean[y]! += 1;
        colMean[x]! += 1;
      }
    }
  }
  for (let y = 0; y < h; y++) rowMean[y] = rowMean[y]! / w;
  for (let x = 0; x < w; x++) colMean[x] = colMean[x]! / h;
  return { rows: bands(rowMean), cols: bands(colMean) };
}

function crop(img: Pixels, x0: number, y0: number, x1: number, y1: number): Pixels {
  const w = x1 - x0;
  const h = y1 - y0;
  const data = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) {
    const from = ((y0 + y) * img.width + x0) * 4;
    data.set(img.data.subarray(from, from + w * 4), y * w * 4);
  }
  return { data, width: w, height: h };
}

/** Square max (dilate) or min (erode) filter of radius r, separable. */
function morph(mask: Uint8Array, w: number, h: number, r: number, max: boolean): Uint8Array {
  const tmp = new Uint8Array(w * h);
  const out = new Uint8Array(w * h);
  const pick = max ? 1 : 0;
  // Outside the image counts as ground for a dilate and as stamp-free for an erode.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = max ? 0 : 1;
      for (let k = -r; k <= r; k++) {
        const xx = x + k;
        const s = xx < 0 || xx >= w ? 0 : mask[y * w + xx]!;
        if (s === pick) {
          v = pick;
          break;
        }
      }
      tmp[y * w + x] = v;
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = max ? 0 : 1;
      for (let k = -r; k <= r; k++) {
        const yy = y + k;
        const s = yy < 0 || yy >= h ? 0 : tmp[yy * w + x]!;
        if (s === pick) {
          v = pick;
          break;
        }
      }
      out[y * w + x] = v;
    }
  }
  return out;
}

/** Flood the ground in from every edge pixel through `open` pixels (4-connected). */
function floodFromEdges(open: Uint8Array, w: number, h: number): Uint8Array {
  const filled = new Uint8Array(w * h);
  const stack: number[] = [];
  const push = (i: number) => {
    if (open[i] && !filled[i]) {
      filled[i] = 1;
      stack.push(i);
    }
  };
  for (let x = 0; x < w; x++) {
    push(x);
    push((h - 1) * w + x);
  }
  for (let y = 0; y < h; y++) {
    push(y * w);
    push(y * w + w - 1);
  }
  while (stack.length) {
    const i = stack.pop()!;
    const x = i % w;
    if (x > 0) push(i - 1);
    if (x < w - 1) push(i + 1);
    if (i >= w) push(i - w);
    if (i < w * (h - 1)) push(i + w);
  }
  return filled;
}

/**
 * Keep only the largest 4-connected piece of a mask. A speck of paper-coloured
 * ground beside the stamp would otherwise survive the fill and widen the crop.
 */
export function largestPiece(mask: Uint8Array, w: number, h: number): Uint8Array {
  const label = new Int32Array(w * h);
  let best = 0;
  let bestSize = 0;
  let next = 0;
  const stack: number[] = [];
  for (let s = 0; s < w * h; s++) {
    if (!mask[s] || label[s]) continue;
    next++;
    let size = 0;
    label[s] = next;
    stack.push(s);
    while (stack.length) {
      const i = stack.pop()!;
      size++;
      const x = i % w;
      for (const j of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]) {
        if (j >= 0 && j < w * h && mask[j] && !label[j]) {
          label[j] = next;
          stack.push(j);
        }
      }
    }
    if (size > bestSize) {
      bestSize = size;
      best = next;
    }
  }
  const out = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) out[i] = label[i] === best && best > 0 ? 1 : 0;
  return out;
}

/** A binary mask feathered by a small gaussian, as 0..1 alpha. */
function feather(mask: Uint8Array, w: number, h: number, sigma: number): Float32Array {
  const r = Math.max(1, Math.ceil(sigma * 3));
  const kernel: number[] = [];
  let sum = 0;
  for (let k = -r; k <= r; k++) {
    const v = Math.exp(-(k * k) / (2 * sigma * sigma));
    kernel.push(v);
    sum += v;
  }
  for (let k = 0; k < kernel.length; k++) kernel[k] = kernel[k]! / sum;
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = 0;
      for (let k = -r; k <= r; k++) {
        const xx = Math.min(w - 1, Math.max(0, x + k));
        v += mask[y * w + xx]! * kernel[k + r]!;
      }
      tmp[y * w + x] = v;
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = 0;
      for (let k = -r; k <= r; k++) {
        const yy = Math.min(h - 1, Math.max(0, y + k));
        v += tmp[yy * w + x]! * kernel[k + r]!;
      }
      out[y * w + x] = v;
    }
  }
  return out;
}

/** The stamp's mask inside one padded cell: the first threshold that covers enough. */
export function stampMask(cell: Pixels, bg: [number, number, number]): { mask: Uint8Array; rough: boolean } {
  const { width: w, height: h, data } = cell;
  const ground = (bg[0] + bg[1] + bg[2]) / 3;
  const lum = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) lum[i] = (data[i * 4]! + data[i * 4 + 1]! + data[i * 4 + 2]!) / 3;

  // Lighter-than-ground first (the original sheets, and their shadows drop out
  // cleanly); then differs-from-ground, for paper darker than the ground.
  const tests: ((i: number) => boolean)[] = [
    ...THRESHOLDS.map((thr) => (i: number) => lum[i]! > ground + thr),
    ...COLOUR_THRESHOLDS.map(
      (thr) => (i: number) =>
        Math.abs(data[i * 4]! - bg[0]) + Math.abs(data[i * 4 + 1]! - bg[1]) + Math.abs(data[i * 4 + 2]! - bg[2]) > thr,
    ),
  ];
  let best: { mask: Uint8Array; cover: number } | null = null;
  for (const isPaper of tests) {
    const paper = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) paper[i] = isPaper(i) ? 1 : 0;
    const grown = morph(paper, w, h, GROW, true);
    const open = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) open[i] = grown[i] ? 0 : 1;
    const outside = floodFromEdges(open, w, h);
    const inside = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) inside[i] = outside[i] ? 0 : 1;
    const mask = morph(inside, w, h, GROW, false);
    let on = 0;
    for (let i = 0; i < w * h; i++) on += mask[i]!;
    const cover = on / (w * h);
    // A mask that fills a side of the crop never found the ground along it
    // (the ground there read as "paper") — not a clean cut, whatever it covers.
    // Checked before the erosion, which always clears the outermost pixels.
    if (cover > COVER && edgeShare(inside, w, h) < 0.5) return { mask, rough: false };
    if (!best || cover > best.cover) best = { mask, cover };
  }
  return { mask: best!.mask, rough: true };
}

/**
 * The largest share of any one side of the crop that is inside the mask. A
 * real stamp sits inside its padding; a side that is mostly mask means the
 * ground along it read as paper (e.g. a brighter strip at the sheet's edge).
 */
function edgeShare(mask: Uint8Array, w: number, h: number): number {
  let top = 0;
  let bottom = 0;
  let left = 0;
  let right = 0;
  for (let x = 0; x < w; x++) {
    top += mask[x]!;
    bottom += mask[(h - 1) * w + x]!;
  }
  for (let y = 0; y < h; y++) {
    left += mask[y * w]!;
    right += mask[y * w + w - 1]!;
  }
  return Math.max(top / w, bottom / w, left / h, right / h);
}

/** Apply a feathered mask and trim to its bounding box. */
function cutOut(cell: Pixels, mask: Uint8Array): Pixels | null {
  const { width: w, height: h } = cell;
  const alpha = feather(mask, w, h, FEATHER);
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (alpha[y * w + x]! > 0.02) {
        if (x < x0) x0 = x;
        if (y < y0) y0 = y;
        if (x > x1) x1 = x;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < x0 || y1 < y0) return null;
  const out = crop(cell, x0, y0, x1 + 1, y1 + 1);
  for (let y = 0; y < out.height; y++) {
    for (let x = 0; x < out.width; x++) {
      const a = alpha[(y + y0) * w + (x + x0)]!;
      const i = (y * out.width + x) * 4 + 3;
      out.data[i] = Math.round(Math.min(1, a) * (out.data[i]! / 255) * 255);
    }
  }
  return out;
}

/** Cut a whole sheet. Cells with nothing left after masking are skipped. */
export function cutSheet(sheet: Pixels): CutStamp[] {
  const bg = groundColour(sheet);
  const { rows, cols } = findGrid(sheet, bg);
  const out: CutStamp[] = [];
  rows.forEach(([ry0, ry1], row) => {
    cols.forEach(([cx0, cx1], col) => {
      const cell = crop(
        sheet,
        Math.max(0, cx0 - PAD),
        Math.max(0, ry0 - PAD),
        Math.min(sheet.width, cx1 + PAD),
        Math.min(sheet.height, ry1 + PAD),
      );
      const { mask, rough } = stampMask(cell, bg);
      const pixels = cutOut(cell, largestPiece(mask, cell.width, cell.height));
      if (pixels && pixels.width >= 20 && pixels.height >= 20) out.push({ pixels, row, col, rough });
    });
  });
  return out;
}
